<template>
  <FreeSlider
    ref="rangeSliderRef"
    :configs="sliderConfig"
    @change:model-value="onChange"
  >
    <template #title>
      <h2 class="text-text-neutral-primary text-lg font-semibold">
        {{ title }}
      </h2>
    </template>
    <template #labels="{ labels }">
      <p
        class="text-text-neutral-tertiary flex w-full justify-between text-xs font-medium"
      >
        <slot
          v-for="(label, idx) in labels"
          :key="idx"
          name="label"
          v-bind="{ label }"
        >
          <span>{{ label }}</span>
        </slot>
      </p>
    </template>
  </FreeSlider>
</template>

<script setup lang="ts">
  import dayjs from 'dayjs';
  import { debounce } from 'lodash-es';
  import { computed, ref, watch } from 'vue';
  import duration from 'dayjs/plugin/duration';
  import { FreeSlider } from 'freedom-shared';

  dayjs.extend(duration);

  const props = defineProps<{
    title: string;
    state: number[];
    limits: number[];
    options?: {
      margin?: number;
      step?: number;
    };
  }>();

  const emit = defineEmits<{
    onUpdate: [range: number[]];
  }>();

  const rangeSliderRef = ref<InstanceType<typeof FreeSlider>>();

  const sliderConfig = computed(() => {
    return {
      start: props.state.length === 2 ? props.state : props.limits,
      range: {
        min: props.limits[0],
        max: props.limits[1],
      },
      connect: [false, true, false],
      step: props.options?.step ?? 100,
      margin: props.options?.margin ?? 100,
    };
  });

  function update(value: number[]) {
    const [min, max] = props.state;
    const [newMin, newMax] = value;

    if (min !== +newMin || max !== +newMax) {
      emit('onUpdate', value);
    }
  }

  const debounceChange = debounce(update, 300);

  function onChange(value: number[]) {
    debounceChange(value.map(Math.floor));
  }

  watch(
    () => props.state,
    newState => {
      rangeSliderRef?.value?.sliderAPI?.set(newState);
    },
  );
</script>
